"use client";
import classNames from "classnames";
import capitalize from "lodash/capitalize";
import isEmpty from "lodash/isEmpty";
import dynamic from "next/dynamic";
import { useContext, useMemo, useState } from "react";

import { useAuth0V2 } from "@stadium/services/auth0";
import { useFlags } from "@stadium/services/launch-darkly";
import { useWithAnalytics } from "@stadium/services/analytics/hooks/use-with-analytics";

import {
  ResponsiveDrawerDropdown,
  DropdownToggle,
} from "@stadium/ui/src/components/bootstrap-dropdown";
import Multimedia from "@stadium/shared/src/components/shared/multimedia";

import { formatPrice } from "@stadium/helpers/currency";
import { formatNumber } from "@stadium/helpers/number";
import { useConfig } from "@stadium/hooks/lib/use-config";
import {
  getConversionRates,
  getCurrencies,
  getWalletSavingPlans,
} from "@stadium/redux-utils/selectors/configSelector";

import Button, { LinkButton } from "@stadium/shared/src/components/shared/button";
import SliderDropdown from "@stadium/ui/src/components/SliderDropdown";

import { isStoreSite } from "@stadium/shared/src/helpers/brands";
import { computeUserFundsSummary } from "@stadium/shared/src/helpers/user-funds";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import {
  getStadiumRouteWithWorkspaceSlug,
  getRecipientSendPointsRoute,
  users,
  eshops,
} from "@stadium/shared/src/constants/routes";

import { DEFAULT_STC_CURRENCY } from "@stadium/constants/currency";
import { TEAM_WALLET_LEARN_MORE_LINK } from "@stadium/constants/external-routes";
import { getSharedWalletAddFundsUrl, getSharedWalletUrl } from "@stadium/constants/user-routes";
import { AUTHORIZED_PAYMENT_OPTIONS, ROLES } from "@stadium/constants/wallets";
import {
  workspaceLaunchFlag,
  workspaceWalletFlag,
} from "@stadium/services/launch-darkly/constants";
import {
  ANALYTICS_EVENT_ACTION_VIEWED,
} from "@stadium/services/analytics/constants/default";

/*stadium-wallet-top-nav is using in top navigation, please don't change it*/
import WalletImage from "@stadium/shared/src/assets/images/stadium-wallet-top-nav.svg";
import redeemGiftCode from "@stadium/shared/src/assets/images/redeem-gift-code.png";

import { useCurrentUserContext } from "@stadium/hooks/lib/use-current-user";
import { useWorkspaceContext } from "@stadium/hooks/lib/workspace";

import { BYSTADIUM_REDEEM_GIFT_CODE_ROUTE } from "@stadium/helpers/app-routes";
import { USE_WALLET_OPTION, USER_FUNDS_ANALYTICS_TARGET } from "@stadium/shared/src/components/user-funds-detail/constants";

import defaultStyles from "./main.module.scss";

const TransferPointsModal = dynamic(() => import("./transfer-points"));

/** `track` is always `true`. Pass a `target` string (e.g. from `USER_FUNDS_ANALYTICS_TARGET`) and optional fields like `action` or `eventProperties`. */
const getEventLocationAnalytics = (target, overrides = {}) => ({
  track: true,
  target,
  ...overrides,
});

const { stadiumWalletKey, stadiumPointsWalletKey, storeSpecificPointsKey } =
  AUTHORIZED_PAYMENT_OPTIONS;

const UserFundsDetail = ({
  storeDetails = {},
  authorizedPayments = {},
  styles = defaultStyles,
  userDetails = {},
}) => {
  const {
    [workspaceWalletFlag]: workspaceWalletFlagEnabled,
    [workspaceLaunchFlag]: workspaceLaunchFlagEnabled,
  } = useFlags();

  const { isAuthenticated } = useAuth0V2();
  const { currentUserData, stadiumPoints: userStadiumPoints } = useCurrentUserContext();
  const { workspaceSlug: dashboardWorkspaceSlug } = useWorkspaceContext();
  const { walletCurrency = {}, teams: oldTeamWallets = [] } = currentUserData;
  const {
    teams: newTeamWallets = [],
    workspacePoints = 0,
    isPersonalWorkspace,
    teamsWithoutDefaultPresent,
  } = userDetails;

  const teamWallets = workspaceLaunchFlagEnabled ? newTeamWallets : oldTeamWallets;
  const stadiumPoints = workspaceLaunchFlagEnabled ? workspacePoints : userStadiumPoints;

  const currencies = useConfig(getCurrencies);
  const walletSavingsPlans = useConfig(getWalletSavingPlans);
  const { isDesktopDevice } = useContext(CurrentDeviceContext);
  const conversionRates = useConfig(getConversionRates);

  const {
    storeCurrencyName,
    storeCurrencyCode,
    storeCurrencyPrefix,
    storeCurrencySuffix,
    storeBalance,
    displayCurrencyCode,
    cartItemsTotal = 0,
    isGuestOrPreviewMode = false,
    isStorePointsMigrationAllowed,
    site,
    previewMode,
    allowSendPointsFromStorefront,
    storeNumber,
    hideBuyPoints,
    workspaceSlug: storeWorkspaceSlug,
  } = storeDetails;

  const workspaceSlug = storeWorkspaceSlug || dashboardWorkspaceSlug;

  const pointsWalletPathname = workspaceWalletFlagEnabled
    ? users.points
    : `${eshops.base}${eshops.myFunds}`;

  const workspacePointsWalletUrl = getStadiumRouteWithWorkspaceSlug({
    workspaceSlug,
    pathname: `${pointsWalletPathname}`,
  });

  const buyPointsUrl = `${workspacePointsWalletUrl}?buyPoints=true`;

  // adding already paid amount to points for payment failure cases.
  const stadiumPointsBalance = useMemo(() => {
    const stadiumPointsPaymentObj = authorizedPayments[stadiumPointsWalletKey];
    if (stadiumPointsPaymentObj) {
      return stadiumPoints + +stadiumPointsPaymentObj.originalAmount;
    }
    return stadiumPoints;
  }, [stadiumPoints, authorizedPayments]);

  // adding already paid amount to wallet for payment failure cases.
  const userWalletBalance = useMemo(() => {
    const originalWalletBalance = parseFloat(currentUserData?.walletBalance);
    const stadiumWalletPaymentObj = authorizedPayments[stadiumWalletKey];
    if (stadiumWalletPaymentObj) {
      return originalWalletBalance + +stadiumWalletPaymentObj.originalAmount;
    }
    return originalWalletBalance;
  }, [currentUserData, authorizedPayments]);

  const storeSpecificPointsBalance = useMemo(() => {
    const storeSpecificPointsPaymentObj = authorizedPayments[storeSpecificPointsKey];
    if (storeSpecificPointsPaymentObj) {
      return +storeBalance + +storeSpecificPointsPaymentObj.originalAmount;
    }
    return +storeBalance;
  }, [storeBalance, authorizedPayments]);

  const formattedStadiumPointsBalance = `${formatNumber(stadiumPointsBalance)} ${
    DEFAULT_STC_CURRENCY.name
  }`;

  const walletSavingPlansMapping = walletSavingsPlans?.reduce((res, plan) => {
    res[plan.paymentMode] = { ...plan };
    return res;
  }, {});

  const { invoice = {} } = walletSavingPlansMapping;
  const { percentage } = invoice;

  const storeSite = isStoreSite(site);

  const isStcStoreCurrency = storeCurrencyCode === DEFAULT_STC_CURRENCY.code;

  const withAnalytics = useWithAnalytics();

  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const toggleTooltip = () => {
    if (!isTooltipVisible) {
      withAnalytics(
        undefined,
        getEventLocationAnalytics(USER_FUNDS_ANALYTICS_TARGET.WALLET, {
          action: ANALYTICS_EVENT_ACTION_VIEWED,
        }),
      )();
    }
    setIsTooltipVisible(!isTooltipVisible);
  };

  const [isTransferPointsModalOpen, setIsTransferPointsModalOpen] = useState(false);
  const closeTransferPointsModal = () => setIsTransferPointsModalOpen(false);
  const openTransferPointsModal = () => setIsTransferPointsModalOpen(true);

  const totalPoints = useMemo(() => {
    if (isStcStoreCurrency) return storeSpecificPointsBalance + stadiumPointsBalance;
    return storeSpecificPointsBalance;
  }, [storeSpecificPointsBalance, stadiumPointsBalance, isStcStoreCurrency]);

  const { isPointsLeft, formattedAmount } = useMemo(
    () =>
      storeCurrencyCode
        ? computeUserFundsSummary({
            storeBalance: totalPoints,
            storeDetails,
            cartItemsTotal,
            displayCurrencyCode,
            conversionRates,
          })
        : {
            isPointsLeft: true,
            formattedAmount: formatPrice(stadiumPointsBalance, {
              suffix: DEFAULT_STC_CURRENCY.name,
            }),
          }, // default currency for dashboard side
    [storeDetails, cartItemsTotal, totalPoints, displayCurrencyCode, conversionRates],
  );

  const walletOptions = useMemo(() => {
    const wallets = [...(storeSpecificPointsBalance ? [] : teamWallets)];
    if (isPersonalWorkspace && workspaceWalletFlagEnabled) {
      wallets.unshift({
        id: USE_WALLET_OPTION.key,
        name: USE_WALLET_OPTION.label,
        currencyCode: walletCurrency.code,
        myWalletBalance: userWalletBalance,
      });
    }
    return wallets;
  }, [
    teamWallets,
    userWalletBalance,
    storeSpecificPointsBalance,
    workspaceWalletFlagEnabled,
    isPersonalWorkspace,
  ]);

  const currencyMapping = useMemo(() => {
    const mapping = {};
    currencies.forEach((currency) => {
      mapping[currency.code] = currency;
    });
    return mapping;
  }, [currencies]);

  const renderTransferPointsBtn = () => (
    <Button
      onClick={openTransferPointsModal}
      styleType="link"
      className={`${styles.learnMoreLink} ${styles.linkNoBorder}`}
      disabled={isGuestOrPreviewMode}
    >
      Transfer to stadium pts
    </Button>
  );

  const renderAddFundsCta = ({ text = "Add Funds", href }) => (
    <LinkButton
      href={href}
      styleType="link"
      className={styles.learnMoreLink}
      disabled={isGuestOrPreviewMode}
    >
      {text}
    </LinkButton>
  );
  const renderTeamWalletOffer = () => {
    const teams = workspaceLaunchFlagEnabled ? teamsWithoutDefaultPresent : teamWallets;
    const shouldShowOffer =
      (workspaceWalletFlagEnabled ? !teams : isEmpty(teamWallets)) &&
      !storeSpecificPointsBalance &&
      percentage;

    return (
      shouldShowOffer && (
        <div className={`${styles.labelOption} ${styles.greenBg} team-wallet`}>
          <div className={styles.label}>
            <span className={styles.labelName}>
              {workspaceWalletFlagEnabled ? "Wallet" : "Shared Wallet"}
            </span>
            <div className={styles.labelValue}>
              Earn up to {formatPrice(percentage)}% on wallet deposits
            </div>
          </div>

          <LinkButton
            href={TEAM_WALLET_LEARN_MORE_LINK}
            styleType="link"
            className={styles.learnMoreLink}
            target="_top"
          >
            Learn More
          </LinkButton>
        </div>
      )
    );
  };

  const renderRedeemGiftCode = () => {
    return (
      <section className={styles.redeemGiftCode} aria-labelledby="redeem-gift-code-title">
        <div className={styles.giftCodelabel}>
          <h2 id="redeem-gift-code-title" className={styles.giftCodelabelName}>
            Redeem Gift Code
          </h2>

          <p className={styles.giftCodelabelValue}>Have a gift code? Redeem it below.</p>

          <LinkButton
            href={BYSTADIUM_REDEEM_GIFT_CODE_ROUTE}
            styleType="link"
            className={styles.redeemGiftCodeLink}
            aria-label="Redeem your gift code"
            target="_top"
            eventLocationAnalytics={getEventLocationAnalytics(USER_FUNDS_ANALYTICS_TARGET.REDEEM)}
          >
            Redeem
          </LinkButton>
        </div>

        <figure className={styles.cardImgWrapper}>
          <Multimedia
            type="image"
            width={85}
            src={redeemGiftCode}
            alt="Gift code redemption illustration"
          />
        </figure>
      </section>
    );
  };

  const renderAvailableBalance = ({ isDisabled = false, label, balance, cta: Cta }) => (
    <div
      className={`${styles.labelOption} ${styles.availableBalanceLinkBlock} ${
        isDisabled ? styles.disabled : ""
      }`}
    >
      <div className={styles.label}>
        <span className={styles.labelName}>{label}</span>
        <div className={`${styles.labelValue} ${previewMode ? styles.balanceText : ""}`}>
          Available Balance: {balance}
        </div>
        {!isDisabled && previewMode && (
          <span className={styles.previewText}>(Amount above is shown for demo purposes only)</span>
        )}
      </div>
      {Cta && <Cta />}
    </div>
  );

  const renderTeamWalletOption = ({
    teamId,
    isDisabled = false,
    label,
    balance,
    hideAddFundsCta,
    showSummary,
  }) =>
    storeSite ? (
      renderAvailableBalance({ teamId, isDisabled, label, balance })
    ) : (
      <LinkButton
        href={getSharedWalletUrl(teamId)}
        className={styles.balanceLink}
        styleType="link"
        disabled={isGuestOrPreviewMode}
      >
        {renderAvailableBalance({
          teamId,
          isDisabled,
          label,
          balance,
          ...(!hideAddFundsCta && {
            cta: () =>
              renderAddFundsCta({ href: getSharedWalletAddFundsUrl(teamId, { showSummary }) }),
          }),
        })}
      </LinkButton>
    );

  const renderTeamWallet = ({
    id,
    name,
    currencyCode,
    myWalletBalance,
    currentUserRole,
    isCurrentUserOwner,
    permissions = {},
    defaultTeam,
  }) => {
    const prefix = currencyMapping[currencyCode]?.symbol || currencyCode,
      balance = formatPrice(myWalletBalance || 0, { prefix });
    const isTeamWallet = id !== USE_WALLET_OPTION.key;

    const label = isTeamWallet && !workspaceWalletFlagEnabled ? `${name} Shared Wallet` : name;

    const isAdminOrOwner = isCurrentUserOwner || currentUserRole === ROLES.admin;
    const hideAddFundsCta =
      isTeamWallet &&
      !(workspaceWalletFlagEnabled && workspaceLaunchFlagEnabled
        ? permissions["add_funds"]
        : isAdminOrOwner);

    return renderTeamWalletOption({
      ...(isTeamWallet && { teamId: id }),
      label,
      balance,
      isDisabled: !myWalletBalance,
      hideAddFundsCta,
      showSummary:
        !defaultTeam &&
        workspaceWalletFlagEnabled &&
        (workspaceLaunchFlagEnabled ? permissions["view_members"] : isAdminOrOwner),
    });
  };

  const renderStadiumPointOption = ({ isDisabled = false, label, balance }) => {
    return hideBuyPoints || isGuestOrPreviewMode ? (
      renderAvailableBalance({ isDisabled, label, balance })
    ) : (
      <LinkButton href={workspacePointsWalletUrl} styleType="link" className={styles.balanceLink}>
        {renderAvailableBalance({
          isDisabled,
          label,
          balance,
          cta: () =>
            renderAddFundsCta({
              text: "Buy Points",
              href: buyPointsUrl,
            }),
        })}
      </LinkButton>
    );
  };

  const renderGiftedPointOption = ({ isDisabled = false, label, balance }) =>
    isStorePointsMigrationAllowed ? (
      <Button
        onClick={openTransferPointsModal}
        styleType="link"
        disabled={isGuestOrPreviewMode}
        className={`${styles.btnLink} ${styles.btnGiftLink}`}
      >
        <div className={`${styles.labelOption} ${isDisabled ? styles.disabled : ""}`}>
          {renderAvailableBalance({ isDisabled, label, balance })}
          {renderTransferPointsBtn()}
        </div>
      </Button>
    ) : (
      renderAvailableBalance({ isDisabled, label, balance })
    );

  const renderDropDownContent = () => {
    return (
      <>
        {!!storeSpecificPointsBalance &&
          renderGiftedPointOption({
            label: `Gifted ${storeCurrencyName} in this shop`,
            balance: formatPrice(storeSpecificPointsBalance, {
              prefix: storeCurrencyPrefix,
              suffix: storeCurrencySuffix,
              currencyCode: storeCurrencyCode,
            }),
          })}
        {renderStadiumPointOption({
          isDisabled: !stadiumPointsBalance,
          label: "Stadium Points",
          balance: formattedStadiumPointsBalance,
        })}
        {walletOptions.map(renderTeamWallet)}
        {renderTeamWalletOffer()}
        {renderRedeemGiftCode()}

        {allowSendPointsFromStorefront && (
          <div className={styles.buttonBlock}>
            <div className={styles.wrapper}>
              <LinkButton
                href={getRecipientSendPointsRoute(storeNumber, workspaceSlug)}
                styleType="primary"
                className={styles.btnSendPoint}
                eventLocationAnalytics={getEventLocationAnalytics(
                  USER_FUNDS_ANALYTICS_TARGET.SEND_CTA,
                )}
              >
                Send {storeCurrencyName}
              </LinkButton>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderUserFundsSummary = () => {
    const currencyName = capitalize(storeCurrencyName || DEFAULT_STC_CURRENCY.label);
    const summaryLabel = isPointsLeft ? `Total ${currencyName}` : "You Pay";
    const summaryMobileLabel = isPointsLeft ? `Total ${currencyName}: ` : "You Pay: ";
    return (
      <div className={styles.totalPointWrap}>
        <div
          className={classNames(styles.walletImageWrap, { [styles.rotateImage]: isTooltipVisible })}
        >
          <Multimedia
            alt="wallet Svg"
            type="image"
            src={WalletImage}
            className={styles.walletImage}
          />
        </div>
        <div className={styles.totalPointValueContainer}>
          <div className={styles.totalPointHeading}>
            {!isDesktopDevice ? "Funds" : summaryLabel}
          </div>
          <div className={styles.totalPointValue}>
            {!isDesktopDevice ? summaryMobileLabel : ""}
            {formattedAmount}
          </div>
        </div>
        <i
          className={classNames(styles.iconArrow, {
            [styles.iconArrowUp]: isTooltipVisible,
          })}
        />
      </div>
    );
  };

  const renderUserFunds = () => {
    if (!isDesktopDevice) {
      return (
        <>
          {" "}
          <SliderDropdown
            styles={styles}
            label={renderUserFundsSummary()}
            className={styles.pointBalanceResponsiveWrapper}
            onLabelClick={toggleTooltip}
            onBackLabelClick={toggleTooltip}
          >
            {renderDropDownContent()}
          </SliderDropdown>
        </>
      );
    }

    return (
      <ResponsiveDrawerDropdown
        label={
          <DropdownToggle className={styles.dropdownToggleBtn} showDropdownIcon={false}>
            {renderUserFundsSummary()}
          </DropdownToggle>
        }
        menuClassName={styles.walletTooltipContainer}
        className={styles.walletDropdown}
        onToggle={toggleTooltip}
      >
        {renderDropDownContent()}
      </ResponsiveDrawerDropdown>
    );
  };

  if (!previewMode && !isAuthenticated) return null;

  return (
    <div className={styles.userFundContainer}>
      {renderUserFunds()}
      {isTransferPointsModalOpen && (
        <TransferPointsModal closeModal={closeTransferPointsModal} data={storeDetails} />
      )}
    </div>
  );
};

export default UserFundsDetail;
